import { Directive, ElementRef, inject, OnDestroy } from '@angular/core';

/**
 * Smoothly animates an element's height whenever its content size changes.
 * Uses ResizeObserver to detect size changes, debounces rapid-fire events
 * (e.g. during Angular animations), then animates from the captured
 * previous height to the settled new height.
 *
 * Usage: add `umssAnimateHeight` to any block element.
 *
 * @example
 * ```html
 * <div umssAnimateHeight>...</div>
 * ```
 */
@Directive({
  selector: '[umssAnimateHeight]',
  standalone: true
})
export class AnimateHeightDirective implements OnDestroy {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private stableHeight: number | null = null;
  private capturedHeight: number | null = null;
  private currentAnimation: Animation | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly observer: ResizeObserver;

  constructor() {
    this.observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;

        // On first change from stable, capture where we're coming from
        if (this.capturedHeight === null && this.stableHeight !== null) {
          this.capturedHeight = this.stableHeight;
        }

        this.stableHeight = newHeight;

        // Debounce: wait for resizes to settle before animating
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.animateToCurrentHeight();
          this.debounceTimer = null;
        }, 50);
      }
    });

    this.observer.observe(this.el.nativeElement);
  }

  private animateToCurrentHeight(): void {
    const from = this.capturedHeight;
    const to = this.stableHeight;
    this.capturedHeight = null;

    if (from === null || to === null || Math.abs(from - to) <= 1) return;

    this.currentAnimation?.cancel();
    this.currentAnimation = this.el.nativeElement.animate(
      [{ height: `${from}px` }, { height: `${to}px` }],
      {
        duration: 300,
        easing: 'cubic-bezier(0.35, 0, 0.25, 1)',
        fill: 'none'
      }
    );
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.observer.disconnect();
    this.currentAnimation?.cancel();
  }
}
